import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Mail, RefreshCw, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const PendingApprovalPage = () => {
  const { user, logout, api, refreshUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const pollIntervalRef = useRef(null);

  // Auto-poll for approval status every 5 seconds
  useEffect(() => {
    const checkApprovalStatus = async () => {
      try {
        const response = await api.get('/auth/me');
        const userData = response.data;
        
        if (userData.approval_status === 'approved') {
          // Stop polling
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          toast.success('Your account has been approved!');
          // Refresh the user data in context
          if (refreshUser) {
            await refreshUser();
          }
          // Force reload to ensure all state is updated properly
          window.location.href = '/';
        } else if (userData.approval_status === 'rejected') {
          // Stop polling
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          toast.error('Your account has been rejected. Please contact support.');
          logout();
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    };

    // Start polling every 5 seconds
    pollIntervalRef.current = setInterval(checkApprovalStatus, 5000);

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [api, refreshUser, logout]);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      // Call the /auth/me endpoint to get fresh user data
      const response = await api.get('/auth/me');
      const userData = response.data;
      
      if (userData.approval_status === 'approved') {
        toast.success('Your account has been approved!');
        // Refresh the user data in context and wait for it
        if (refreshUser) {
          await refreshUser();
        }
        // Force reload to ensure all state is updated properly
        window.location.href = '/';
      } else if (userData.approval_status === 'rejected') {
        toast.error('Your account has been rejected. Please contact support.');
        logout();
      } else {
        toast.info('Your account is still pending approval.');
      }
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Failed to check status. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="bg-slate-900 border-slate-800 max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-4 bg-yellow-600/20 rounded-full w-fit">
            <Clock className="h-12 w-12 text-yellow-500" />
          </div>
          <CardTitle className="text-white text-2xl">Account Pending Approval</CardTitle>
          <CardDescription className="text-slate-400">
            Your admin account is awaiting approval from the super administrator.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-800 p-4 rounded-lg space-y-2">
            <p className="text-slate-400 text-sm">Account Details</p>
            <p className="text-white font-medium">{user?.name}</p>
            <p className="text-slate-300 text-sm flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {user?.email}
            </p>
          </div>

          <div className="text-center text-slate-400 text-sm">
            <p>You will be able to access the dashboard once your account has been approved.</p>
            <p className="mt-2">Please check back later or contact the administrator.</p>
          </div>

          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleCheckStatus}
              disabled={checking}
              className="w-full bg-blue-600 hover:bg-blue-500"
            >
              {checking ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {checking ? 'Checking...' : 'Check Status'}
            </Button>
            <Button 
              variant="outline" 
              onClick={logout}
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PendingApprovalPage;
